import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaPlay, FaPause, FaTrash } from 'react-icons/fa';
import customAgentService from '../../../services/customAgent';
import TemplateGallery from '../../../components/custom-agent/TemplateGallery';
import { useToast } from '../../../contexts/ToastContext';
import './CustomAgentList.css';

const CustomAgentList = ({ showTemplates = false, onShowTemplatesChange = null }) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [customAgents, setCustomAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, draft, paused

  useEffect(() => {
    loadCustomAgents();
  }, [filter]);

  const loadCustomAgents = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await customAgentService.listCustomAgents(params);
      setCustomAgents(response.data || []);
    } catch (err) {
      console.error('Error loading custom agents:', err);
      showError('Failed to load custom agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/app/wizard/custom-agents/new');
  };

  const handleEdit = (customAgentId) => {
    navigate(`/app/wizard/custom-agents/${customAgentId}`);
  };

  const handleDelete = async (customAgentId) => {
    if (!window.confirm('Are you sure you want to delete this custom agent?')) {
      return;
    }

    try {
      await customAgentService.deleteCustomAgent(customAgentId);
      loadCustomAgents();
    } catch (err) {
      console.error('Error deleting custom agent:', err);
      alert('Failed to delete custom agent');
    }
  };


  return (
    <div className="custom-agent-list-container">
      <div className="custom-agent-list-header">
      </div>

      <div className="custom-agent-filters">
        <button
          className={`custom-agent-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={`custom-agent-filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button
          className={`custom-agent-filter-btn ${filter === 'draft' ? 'active' : ''}`}
          onClick={() => setFilter('draft')}
        >
          Draft
        </button>
        <button
          className={`custom-agent-filter-btn ${filter === 'paused' ? 'active' : ''}`}
          onClick={() => setFilter('paused')}
        >
          Paused
        </button>
      </div>

      {loading ? (
        <div className="custom-agent-list-loading">
          <div className="spinner"></div>
          <p>Loading custom agents...</p>
        </div>
      ) : customAgents.length === 0 ? (
        <div className="custom-agent-empty-state">
          <h3>No custom agents yet</h3>
          <button className="btn-create-first" onClick={handleCreate}>
            <FaPlus /> Create Your First Custom Agent
          </button>
        </div>
      ) : (
        <div className="custom-agent-grid">
          {customAgents.map((customAgent) => (
            <div key={customAgent.id} className="custom-agent-card">
              <div className="custom-agent-card-header">
                <h3>{customAgent.name}</h3>
                <span className={`custom-agent-badge custom-agent-badge-${customAgent.status}`}>
                  {customAgent.status}
                </span>
              </div>

              {customAgent.description && (
                <p className="custom-agent-description">{customAgent.description}</p>
              )}

              <div className="custom-agent-meta">
                <span>Updated: {new Date(customAgent.updatedAt).toLocaleDateString()}</span>
              </div>

              {customAgent.lastExecutedAt && (
                <div className="custom-agent-last-run">
                  Last run: {new Date(customAgent.lastExecutedAt).toLocaleString()}
                </div>
              )}

              <div className="custom-agent-actions">
                <button
                  className="action-btn action-btn-primary"
                  onClick={() => handleEdit(customAgent.id)}
                >
                  Edit
                </button>
                <button
                  className="action-btn action-btn-danger"
                  onClick={() => handleDelete(customAgent.id)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Gallery */}
      <TemplateGallery 
        isOpen={showTemplates}
        onClose={() => {
          if (onShowTemplatesChange) {
            onShowTemplatesChange(false);
          }
        }}
      />
    </div>
  );
};

export default CustomAgentList;
